import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { employeeAPI, departmentAPI, managerDashboardAPI, companyAPI, shiftAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { getInitials, formatCurrency } from '../../lib/utils';
import { Plus, Search, UserPlus, Mail, Phone, AlertTriangle, ArrowRightLeft, Building2, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

const EmployeeManagement = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    department_id: '',
    manager_id: '',
    default_shift_template_id: '',
    employee_id: '',
    phone: '',
    monthly_salary: ''
  });
  
  // Transfer dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferEmployee, setTransferEmployee] = useState(null);
  const [targetCompanyId, setTargetCompanyId] = useState('');
  const [targetDepartments, setTargetDepartments] = useState([]);
  const [targetManagers, setTargetManagers] = useState([]);
  const [targetShifts, setTargetShifts] = useState([]);
  const [transferData, setTransferData] = useState({
    new_department_id: '',
    new_manager_id: '',
    new_shift_template_id: '',
    effective_date: format(new Date(), 'yyyy-MM-dd'),
    reason: ''
  });

  const fetchData = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const [empRes, deptRes, mgrRes, compRes, shiftRes] = await Promise.all([
        employeeAPI.getAll(user.company_id),
        departmentAPI.getAll(user.company_id),
        managerDashboardAPI.getManagers(user.company_id),
        companyAPI.getTransferTargets().catch(() => ({ data: [] })),
        shiftAPI.getAll(user.company_id)
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      setManagers(mgrRes.data);
      setShifts(shiftRes.data);
      // Filter out current company from transfer options
      setCompanies(compRes.data.filter(c => c.id !== user.company_id && c.is_active));
    } catch (err) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    
    // Warn if no manager assigned for employee role
    if (newEmployee.role === 'employee' && !newEmployee.manager_id) {
      toast.warning('Consider assigning a manager for this employee');
    }
    
    try {
      const data = {
        ...newEmployee,
        monthly_salary: newEmployee.monthly_salary ? parseFloat(newEmployee.monthly_salary) : null
      };
      await employeeAPI.create(user.company_id, data);
      toast.success('Employee added successfully');
      setIsAddOpen(false);
      setNewEmployee({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'employee',
        department_id: '',
        manager_id: '',
        default_shift_template_id: '',
        employee_id: '',
        phone: '',
        monthly_salary: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add employee');
    }
  };

  const openTransferDialog = (emp) => {
    setTransferEmployee(emp);
    setTargetCompanyId('');
    setTargetDepartments([]);
    setTargetManagers([]);
    setTargetShifts([]);
    setTransferData({
      new_department_id: '',
      new_manager_id: '',
      new_shift_template_id: '',
      effective_date: format(new Date(), 'yyyy-MM-dd'),
      reason: ''
    });
    setTransferDialogOpen(true);
  };

  const handleTargetCompanyChange = async (companyId) => {
    setTargetCompanyId(companyId);
    setTransferData(prev => ({
      ...prev,
      new_department_id: '',
      new_manager_id: '',
      new_shift_template_id: ''
    }));
    
    if (companyId) {
      try {
        const [deptRes, mgrRes, shiftRes] = await Promise.all([
          departmentAPI.getAll(companyId),
          managerDashboardAPI.getManagers(companyId),
          shiftAPI.getAll(companyId)
        ]);
        setTargetDepartments(deptRes.data);
        setTargetManagers(mgrRes.data);
        setTargetShifts(shiftRes.data);
      } catch (error) {
        console.error('Error loading target company data:', error);
      }
    }
  };

  const handleTransfer = async () => {
    if (!transferEmployee || !targetCompanyId) {
      toast.error('Please select a target company');
      return;
    }
    
    try {
      await employeeAPI.transfer({
        employee_id: transferEmployee.id,
        target_company_id: targetCompanyId,
        ...transferData
      });
      toast.success('Employee transferred successfully');
      setTransferDialogOpen(false);
      setTransferEmployee(null);
      fetchData();
    } catch (error) {
      console.error('Error transferring employee:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer employee');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const search = searchTerm.toLowerCase();
    return (
      emp.first_name?.toLowerCase().includes(search) ||
      emp.last_name?.toLowerCase().includes(search) ||
      emp.email?.toLowerCase().includes(search) ||
      emp.employee_id?.toLowerCase().includes(search)
    );
  });

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || '-';
  };

  if (loading) {
    return (
      <DashboardLayout title="Employee Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Employee Management" 
      subtitle={`${employees.length} employees`}
    >
      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="employee-search"
          />
        </div>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-employee-btn">
              <UserPlus className="h-4 w-4" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddEmployee} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={newEmployee.first_name}
                    onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={newEmployee.last_name}
                    onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={newEmployee.password}
                  onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={newEmployee.role}
                    onValueChange={(value) => setNewEmployee({...newEmployee, role: value})}
                  >
                    <SelectTrigger data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select
                    value={newEmployee.department_id}
                    onValueChange={(value) => setNewEmployee({...newEmployee, department_id: value})}
                  >
                    <SelectTrigger data-testid="department-select">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee ID</Label>
                  <Input
                    value={newEmployee.employee_id}
                    onChange={(e) => setNewEmployee({...newEmployee, employee_id: e.target.value})}
                    placeholder="e.g. EMP-0001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Salary</Label>
                  <Input
                    type="number"
                    value={newEmployee.monthly_salary}
                    onChange={(e) => setNewEmployee({...newEmployee, monthly_salary: e.target.value})}
                    placeholder="e.g. 25000"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Manager
                  {newEmployee.role === 'employee' && !newEmployee.manager_id && (
                    <span className="text-orange-500 text-xs flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Recommended
                    </span>
                  )}
                </Label>
                <Select
                  value={newEmployee.manager_id}
                  onValueChange={(value) => setNewEmployee({...newEmployee, manager_id: value})}
                >
                  <SelectTrigger data-testid="manager-select">
                    <SelectValue placeholder="Assign a manager..." />
                  </SelectTrigger>
                  <SelectContent>
                    {managers.map(mgr => (
                      <SelectItem key={mgr.id} value={mgr.id}>
                        {mgr.first_name} {mgr.last_name} ({mgr.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Shift Schedule</Label>
                <Select
                  value={newEmployee.default_shift_template_id}
                  onValueChange={(value) => setNewEmployee({...newEmployee, default_shift_template_id: value})}
                >
                  <SelectTrigger data-testid="shift-select">
                    <SelectValue placeholder="Assign a shift..." />
                  </SelectTrigger>
                  <SelectContent>
                    {shifts.map(shift => (
                      <SelectItem key={shift.id} value={shift.id}>
                        {shift.name} ({shift.start_time} - {shift.end_time})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-employee-btn">Add Employee</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Salary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.map((emp) => (
                <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                  <TableCell>
                    <Link to={`/app/hris/employees/${emp.id}`} className="flex items-center gap-3 hover:opacity-80">
                      <Avatar>
                        <AvatarImage src={emp.avatar_url} />
                        <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-primary hover:underline">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-muted-foreground">{emp.email}</p>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{emp.employee_id || '-'}</code>
                  </TableCell>
                  <TableCell>{getDepartmentName(emp.department_id)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{emp.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {emp.phone && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {emp.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{formatCurrency(emp.monthly_salary)}</TableCell>
                  <TableCell>
                    <Badge className={emp.is_active ? 'status-approved' : 'status-rejected'}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/app/hris/employees/${emp.id}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="View HRIS Profile"
                          data-testid={`view-profile-btn-${emp.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      {companies.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openTransferDialog(emp)}
                          title="Transfer to another company"
                          data-testid="transfer-employee-btn"
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredEmployees.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No employees found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transfer Employee Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              Transfer Employee
            </DialogTitle>
            <DialogDescription>
              Transfer {transferEmployee?.first_name} {transferEmployee?.last_name} to another company
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Current Company</p>
              <p className="font-medium">{user?.company_name || 'Current Company'}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Target Company *</Label>
              <Select value={targetCompanyId} onValueChange={handleTargetCompanyChange}>
                <SelectTrigger data-testid="target-company-select">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {company.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {targetCompanyId && (
              <>
                <div className="space-y-2">
                  <Label>New Department</Label>
                  <Select 
                    value={transferData.new_department_id} 
                    onValueChange={(value) => setTransferData({...transferData, new_department_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {targetDepartments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>New Manager</Label>
                  <Select 
                    value={transferData.new_manager_id} 
                    onValueChange={(value) => setTransferData({...transferData, new_manager_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select manager (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {targetManagers.map(mgr => (
                        <SelectItem key={mgr.id} value={mgr.id}>
                          {mgr.first_name} {mgr.last_name} ({mgr.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>New Shift</Label>
                  <Select 
                    value={transferData.new_shift_template_id} 
                    onValueChange={(value) => setTransferData({...transferData, new_shift_template_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shift (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {targetShifts.map(shift => (
                        <SelectItem key={shift.id} value={shift.id}>
                          {shift.name} ({shift.start_time} - {shift.end_time})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Effective Date *</Label>
              <Input 
                type="date"
                value={transferData.effective_date}
                onChange={(e) => setTransferData({...transferData, effective_date: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea 
                value={transferData.reason}
                onChange={(e) => setTransferData({...transferData, reason: e.target.value})}
                placeholder="Reason for transfer (optional)"
                rows={2}
              />
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Historical records (attendance, leaves, payroll) will be preserved under the current company before the effective date.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={!targetCompanyId} data-testid="confirm-transfer-btn">
              Transfer Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default EmployeeManagement;
